import classNames from 'classnames';
import React, { useState, useRef, useEffect, ReactNode } from 'react';

/**
 * TextTruncate component that truncates text after a specified number of lines
 * and displays a "See More" button to expand the content.
 */
export const SeeMoreTextCollapsible = ({
  children,
  maxLines = 4,
  expandText = 'See More',
  collapseText = 'See Less',
  className = '',
  backgroundColor = 'white',
  ctaClassName = '',
  wrapperClassName = '',
}: {
  children: ReactNode;
  maxLines?: number;
  expandText?: string;
  collapseText?: string;
  className?: string;
  backgroundColor?: string;
  ctaClassName?: string;
  wrapperClassName?: string;
}) => {
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [isTruncated, setIsTruncated] = useState<boolean>(false);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Check if content needs to be truncated
    const checkTruncation = () => {
      if (contentRef.current) {
        const element = contentRef.current;

        // Skip check if element is not visible (e.g., inside closed accordion)
        if (element.scrollHeight === 0) {
          return;
        }

        // Direct comparison: when -webkit-line-clamp is applied, clientHeight reflects
        // the clamped/visible height, while scrollHeight reflects the full content height.
        // If scrollHeight > clientHeight, content is genuinely truncated.
        const isContentTruncated = element.scrollHeight > element.clientHeight;

        setIsTruncated(isContentTruncated);
      }
    };

    // Initial check after a brief delay
    const timeoutId = setTimeout(checkTruncation, 50);

    // Use ResizeObserver to detect when element becomes visible (e.g., accordion opens)
    // This fires when the element's size changes from 0 to actual dimensions
    let resizeObserver: ResizeObserver | null = null;
    if (contentRef.current) {
      resizeObserver = new ResizeObserver(() => {
        // Small delay to let animations complete
        setTimeout(checkTruncation, 50);
      });
      resizeObserver.observe(contentRef.current);
    }

    return () => {
      clearTimeout(timeoutId);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [children, maxLines, isExpanded]);

  const toggleExpand = (): void => {
    setIsExpanded(!isExpanded);
  };

  // If we're showing the full content
  if (isExpanded) {
    return (
      <div className={className}>
        <div>
          {children}

          {isTruncated && (
            <button
              onClick={toggleExpand}
              className={classNames(
                'p-0 bg-transparent underline text-brown-prime focus:outline-none cursor-pointer block',
                ctaClassName
              )}
              style={{ fontFamily: 'inherit', fontSize: 'inherit', fontWeight: 'inherit' }}
              type="button"
              aria-expanded="true"
            >
              {collapseText}
            </button>
          )}
        </div>
      </div>
    );
  }

  // The special case for truncated content - we need to handle ellipsis and button
  return (
    <div className={className}>
      {/* The key is to create two overlapping elements */}
      <div className="relative">
        {/* The visible truncated content */}
        <div
          ref={contentRef}
          className="truncated-content"
          style={{
            display: '-webkit-box',
            WebkitLineClamp: maxLines,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {children}
        </div>

        {isTruncated && (
          <>
            {/* Create an overlay that hides the browser's ellipsis */}
            <div
              className={classNames('absolute bottom-[0px]', wrapperClassName)}
              style={{
                right: '0',
                paddingLeft: '4px',
                backgroundColor,
                display: 'inline-flex',
                alignItems: 'center',
                fontFamily: 'inherit',
                fontSize: 'inherit',
                fontWeight: 'inherit',
              }}
            >
              <span className={ctaClassName}>...</span>{' '}
              <button
                onClick={toggleExpand}
                className={classNames(
                  'bg-transparent underline text-brown-prime hover:underline focus:outline-none cursor-pointer',
                  ctaClassName
                )}
                style={{ fontFamily: 'inherit', fontSize: 'inherit', fontWeight: 'inherit' }}
                type="button"
                aria-expanded="false"
              >
                {expandText}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
